import secureApi from '../../utils/secureApi';
import { getImageUrl } from '../../utils/imageUrl';

export interface DashboardStats {
  teams: number;
  players: number;
  tournaments: number;
  payments: number;
  activePlayers: number;
  inactivePlayers: number;
  activeTeams: number;
  inactiveTeams: number;
  // New fields for Super Admin Dashboard
  totalAmount?: number;
  totalDues?: number;
  totalPlayers?: number;
  approvedPlayers?: number;
  rejectedPlayers?: number;
}

export interface Tournament {
  _id: string;
  name: string;
  sport: string;
  ageCategories: string[];
  startDate: string;
  location: string;
  status: 'upcoming' | 'ongoing' | 'completed';
  image?: string;
  registeredTeams?: number;
  maxTeams?: number;
  injuredPlayers?: number;
  isTeamRegistered?: boolean; // Add the isTeamRegistered field
}

export interface Transaction {
  _id: string;
  transactionId: string;
  tournamentName: string;
  teamName: string;
  date: string;
  ageCategory: string;
  amount: number;
  paymentMode: string;
  status: 'success' | 'failure' | 'pending' | 'paid';
}

export interface DashboardData {
  stats: DashboardStats;
  upcomingTournaments: Tournament[];
  ongoingTournaments: Tournament[];
  recentTransactions: Transaction[];
  recentPlayers: any[];
  recentTeams?: any[];
  recentClubs?: any[];
}

// Normalize a logo S3 key or URL to an absolute URL
const normalizeLogoUrl = (raw: any): string => {
  const candidate = raw?.logo || raw?.teamLogo || raw?.tournamentLogo || raw?.logoUrl || '';
  if (!candidate) return '';
  return getImageUrl(candidate);
};

// Test function to check API endpoint
export const testDashboardAPI = async (clubId: string) => {
  try {
    console.log('🧪 Testing Dashboard API Endpoint');
    console.log('🧪 Club ID:', clubId);
    console.log('🧪 Full URL:', `/api/v1/club/dashboard/count/${clubId}`);

    // Test the endpoint using secureApi
    const response = await secureApi.get<any>(`/api/v1/club/dashboard/count/${clubId}`);
    
    console.log('🧪 API Test Success!');
    console.log('🧪 Response:', response);
    
    // Handle API response structure
    if (response && response.data) {
      return response.data;
    }
    return response;
  } catch (error: any) {
    console.error('🧪 API Test Failed!');
    console.error('🧪 Error:', error.message);
    console.error('🧪 Status:', error.response?.status);
    console.error('🧪 Response:', error.response?.data);
    return null;
  }
};

// Fetch dashboard stats for a club
export const getDashboardStats = async (clubId: string): Promise<DashboardStats> => {
  try {
    const response = await secureApi.get<any>(`/api/v1/club/dashboard/count/${clubId}`);
    
    // secureApi returns response.data automatically
    // Handle different response structures
    let statsData: any;
    if (response && response.statusCode === 200 && response.status === 'success') {
      statsData = response.data || response;
    } else if (response && response.data) {
      statsData = response.data;
    } else {
      statsData = response;
    }
    
    
    // Map the API response to our DashboardStats interface
    // Using the actual API response field names
    const mappedStats: DashboardStats = {
      teams: statsData.teamCount || statsData.teams || statsData.totalTeams || 0,
      players: statsData.playerCount || statsData.players || statsData.totalPlayers || 0,
      tournaments: statsData.tournamentCount || statsData.tournaments || statsData.totalTournaments || 0,
      payments: statsData.paymentCount || statsData.payments || statsData.totalPayments || 0,
      activePlayers: statsData.activePlayerCount || statsData.activePlayers || statsData.activePlayersCount || 0,
      inactivePlayers: statsData.inactivePlayerCount || statsData.inactivePlayers || statsData.inactivePlayersCount || 0,
      activeTeams: statsData.activeTeamCount || statsData.activeTeams || statsData.activeTeamsCount || 0,
      inactiveTeams: statsData.inactiveTeamCount || statsData.inactiveTeams || statsData.inactiveTeamsCount || 0
    };
    
    console.log('  - teams:', mappedStats.teams, '(from:', statsData.teamCount || statsData.teams || statsData.totalTeams, ')');
    console.log('  - players:', mappedStats.players, '(from:', statsData.playerCount || statsData.players || statsData.totalPlayers, ')');
    console.log('  - tournaments:', mappedStats.tournaments, '(from:', statsData.tournamentCount || statsData.tournaments || statsData.totalTournaments, ')');
    console.log('  - payments:', mappedStats.payments, '(from:', statsData.paymentCount || statsData.payments || statsData.totalPayments, ')');
    console.log('  - activePlayers:', mappedStats.activePlayers, '(from:', statsData.activePlayerCount || statsData.activePlayers || statsData.activePlayersCount, ')');
    console.log('  - inactivePlayers:', mappedStats.inactivePlayers, '(from:', statsData.inactivePlayerCount || statsData.inactivePlayers || statsData.inactivePlayersCount, ')');
    console.log('  - activeTeams:', mappedStats.activeTeams, '(from:', statsData.activeTeamCount || statsData.activeTeams || statsData.activeTeamsCount, ')');
    console.log('  - inactiveTeams:', mappedStats.inactiveTeams, '(from:', statsData.inactiveTeamCount || statsData.inactiveTeams || statsData.inactiveTeamsCount, ')');
    
    return mappedStats;
    
  } catch (error: any) {
    console.error('❌ Dashboard Stats Debug - API call failed');
    console.error('❌ Dashboard Stats Debug - Error type:', error.constructor.name);
    console.error('❌ Dashboard Stats Debug - Error message:', error.message);
    console.error('❌ Dashboard Stats Debug - Error response status:', error.response?.status);
    console.error('❌ Dashboard Stats Debug - Error response data:', error.response?.data);
    console.error('❌ Dashboard Stats Debug - Error response headers:', error.response?.headers);
    console.error('❌ Dashboard Stats Debug - Request URL:', error.config?.url);
    console.error('❌ Dashboard Stats Debug - Request method:', error.config?.method);
    console.error('❌ Dashboard Stats Debug - Request headers:', error.config?.headers);
    
    // Handle 404 errors as "no data available"
    if (error.response?.status === 404) {
      return {
        teams: 0,
        players: 0,
        tournaments: 0,
        payments: 0,
        activePlayers: 0,
        inactivePlayers: 0,
        activeTeams: 0,
        inactiveTeams: 0
      };
    }
    
    // For other errors, return default values instead of throwing
    console.error('❌ Dashboard Stats Debug - Other error occurred, returning default values');
    return {
      teams: 0,
      players: 0,
      tournaments: 0,
      payments: 0,
      activePlayers: 0,
      inactivePlayers: 0,
      activeTeams: 0,
      inactiveTeams: 0
    };
  }
};

// Fetch dashboard stats for super admin using new API endpoint
export const getSuperAdminDashboardStats = async (): Promise<DashboardStats> => {
  try {
    const response = await secureApi.get<any>(`/api/v1/tournament-registration/dashboard/stats`);
    
    // Handle API response structure: may be wrapped in { statusCode, data: {...} } or direct
    let statsData: any;
    if ((response as any)?.statusCode === 200 && (response as any)?.status === 'success') {
      statsData = (response as any)?.data || response;
    } else if ((response as any)?.data) {
      statsData = (response as any).data;
    } else {
      statsData = response;
    }
    
    
    // Map the new API response to our DashboardStats interface
    // The new API returns: totalAmount, totalDues, totalPlayers, approvedPlayers, rejectedPlayers
    // We need to map these to both existing and new DashboardStats interface fields
    const mappedStats: DashboardStats = {
      // Map totalPlayers to players
      players: statsData.totalPlayers || 0,
      
      // Calculate teams (since not provided in new API, we'll use a calculated value or default)
      // For now, we'll use a ratio based on players or a default value
      teams: Math.ceil((statsData.totalPlayers || 0) / 15) || 0, // Assuming ~15 players per team
      
      // For tournaments, we'll keep the existing logic or use a default
      tournaments: statsData.tournamentCount || statsData.tournaments || statsData.totalTournaments || statsData.total_tournaments || 0,
      
      // Map totalAmount to payments (representing total revenue/payments)
      payments: statsData.totalAmount || 0,
      
      // Map approvedPlayers to activePlayers
      activePlayers: statsData.approvedPlayers || 0,
      
      // Map rejectedPlayers to inactivePlayers
      inactivePlayers: statsData.rejectedPlayers || 0,
      
      // For teams, we'll calculate active/inactive based on player status
      // Active teams = teams with approved players, Inactive teams = teams with rejected players
      activeTeams: Math.ceil((statsData.approvedPlayers || 0) / 15) || 0,
      inactiveTeams: Math.ceil((statsData.rejectedPlayers || 0) / 15) || 0,
      
      // Add new fields directly from API response
      totalAmount: statsData.totalAmount || 0,
      totalDues: statsData.totalDues || 0,
      totalPlayers: statsData.totalPlayers || 0,
      approvedPlayers: statsData.approvedPlayers || 0,
      rejectedPlayers: statsData.rejectedPlayers || 0
    };
    
    console.log('  - totalAmount:', statsData.totalAmount, '→ payments:', mappedStats.payments, '→ totalAmount:', mappedStats.totalAmount);
    console.log('  - totalDues:', statsData.totalDues, '→ totalDues:', mappedStats.totalDues);
    console.log('  - totalPlayers:', statsData.totalPlayers, '→ players:', mappedStats.players, '→ totalPlayers:', mappedStats.totalPlayers);
    console.log('  - approvedPlayers:', statsData.approvedPlayers, '→ activePlayers:', mappedStats.activePlayers, '→ approvedPlayers:', mappedStats.approvedPlayers);
    console.log('  - rejectedPlayers:', statsData.rejectedPlayers, '→ inactivePlayers:', mappedStats.inactivePlayers, '→ rejectedPlayers:', mappedStats.rejectedPlayers);
    console.log('  - calculated teams:', mappedStats.teams);
    console.log('  - calculated activeTeams:', mappedStats.activeTeams);
    console.log('  - calculated inactiveTeams:', mappedStats.inactiveTeams);
    
    return mappedStats;
  } catch (error: any) {
    console.error('Failed to fetch super admin dashboard stats:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    
    // Return default stats on error
    return {
      teams: 0,
      players: 0,
      tournaments: 0,
      payments: 0,
      activePlayers: 0,
      inactivePlayers: 0,
      activeTeams: 0,
      inactiveTeams: 0
    };
  }
};

// Fetch upcoming tournaments for dashboard
export const getUpcomingTournamentsForDashboard = async (): Promise<Tournament[]> => {
  try {
    console.log('Dashboard - API URL:', `/api/v1/tournament-registration/list-all-tournaments`);
    console.log('Dashboard - Status:', 'upcoming');

    const response = await secureApi.get<any>(`/api/v1/tournament-registration/list-all-tournaments`, {
      params: { status: 'upcoming' }
    });
    
    console.log('Dashboard - API response received:', response);
    
    // Handle different response structures (same as tournamentService)
    let tournaments = [] as any[];
    if ((response as any)?.statusCode === 200 && (response as any)?.status === 'success') {
      tournaments = (response as any)?.data?.tournaments || (response as any)?.tournaments || [];
    } else if ((response as any)?.tournaments) {
      // Direct tournaments array in response
      tournaments = (response as any).tournaments;
    } else if (Array.isArray(response)) {
      // Direct array response
      tournaments = response;
    } else {
      console.error('Unexpected response structure:', response);
      throw new Error((response as any).message || 'Failed to fetch tournaments');
    }
    
    console.log('Dashboard - Parsed tournaments:', tournaments);
    
    // Transform the response to match the expected format
    return tournaments.map((tournament: any) => ({
      _id: tournament._id,
      name: tournament.name,
      sport: tournament.sports,
      ageCategories: tournament.ageCategories || [],
      startDate: tournament.startDate,
      location: tournament.location,
      status: tournament.status,
      image: normalizeLogoUrl(tournament),
      thumbnail: tournament.thumbnail, // Add thumbnail field mapping
      registeredTeams: 0, // Default value since not in API response
      maxTeams: 16, // Default value since not in API response
      isTeamRegistered: Boolean(tournament.isTeamRegistered ?? tournament.teamRegistered ?? tournament.isRegistered ?? false) // Handle multiple possible field names
    }));
  } catch (error: any) {
    console.error('Failed to fetch upcoming tournaments:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error status:', error.response?.status);
    console.error('Request URL:', error.config?.url);
    console.error('Request headers:', error.config?.headers);
    
    // Handle 404 errors as "no data available"
    if (error.response?.status === 404) {
      console.log('No upcoming tournaments found in the system - returning empty array');
      return [];
    }
    
    // For other errors, return empty array instead of mock data
    console.error('Other error occurred, returning empty array');
    return [];
  }
};

// Fetch ongoing tournaments for a club
export const getOngoingTournaments = async (clubId: string): Promise<Tournament[]> => {
  try {
    const response = await secureApi.get<any>(`/api/v1/tournament-registration/list-all-tournaments`, {
      params: { status: 'ongoing' }
    });
    
    // Handle different response structures (same as tournamentService)
    let tournaments = [] as any[];
    if ((response as any)?.statusCode === 200 && (response as any)?.status === 'success') {
      tournaments = (response as any)?.data?.tournaments || (response as any)?.tournaments || [];
    } else if ((response as any)?.tournaments) {
      // Direct tournaments array in response
      tournaments = (response as any).tournaments;
    } else if (Array.isArray(response)) {
      // Direct array response
      tournaments = response;
    } else {
      console.error('Unexpected response structure:', response);
      throw new Error((response as any).message || 'Failed to fetch tournaments');
    }
    
    // Transform the response to match the expected format
    return tournaments.map((tournament: any) => ({
      _id: tournament._id,
      name: tournament.name,
      sport: tournament.sports,
      ageCategories: tournament.ageCategories || [],
      startDate: tournament.startDate,
      location: tournament.location,
      status: tournament.status,
      image: normalizeLogoUrl(tournament),
      thumbnail: tournament.thumbnail, // Add thumbnail field mapping
      registeredTeams: 0, // Default value since not in API response
      maxTeams: 16, // Default value since not in API response
      isTeamRegistered: Boolean(tournament.isTeamRegistered ?? tournament.teamRegistered ?? tournament.isRegistered ?? false) // Handle multiple possible field names
    }));
  } catch (error: any) {
    console.error('Failed to fetch ongoing tournaments:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error status:', error.response?.status);
    console.error('Request URL:', error.config?.url);
    console.error('Request headers:', error.config?.headers);
    
    // Handle 404 errors as "no data available"
    if (error.response?.status === 404) {
      console.log('No ongoing tournaments found in the system - returning empty array');
      return [];
    }
    
    // For other errors, return empty array instead of mock data
    console.error('Other error occurred, returning empty array');
    return [];
  }
};

// Fetch latest transactions for a club
export const getLatestTransactions = async (clubId: string): Promise<Transaction[]> => {
  try {
    console.log('Dashboard - API URL:', `/api/v1/payments/tournament-registration/latest-transactions`);

    const response = await secureApi.get<any>(`/api/v1/payments/tournament-registration/latest-transactions`);
    
    console.log('Dashboard - Latest transactions API response received:', response);
    
    // Handle different response structures
    let transactions = [] as any[];
    if ((response as any)?.statusCode === 200 && (response as any)?.status === 'success') {
      transactions = (response as any)?.data?.transactions || (response as any)?.data || [];
    } else if ((response as any)?.transactions) {
      // Direct transactions array in response
      transactions = (response as any).transactions;
    } else if (Array.isArray(response)) {
      // Direct array response
      transactions = response;
    } else {
      console.error('Unexpected response structure for latest transactions:', response);
      throw new Error((response as any).message || 'Failed to fetch latest transactions');
    }
    
    console.log('Dashboard - Parsed transactions:', transactions);
    
    // Transform the response to match the expected format
    return transactions.map((transaction: any) => {
      console.log('Dashboard - Processing transaction:', transaction);
      
      const mappedTransaction = {
        _id: transaction._id || transaction.id || `temp-${Date.now()}`,
        transactionId: transaction.paymentId || transaction.transactionId || transaction.transaction_id || transaction.id || transaction._id || 'N/A',
        tournamentName: transaction.tournamentInfo?.name || transaction.tournamentName || transaction.tournament_name || transaction.tournament || 'N/A',
        teamName: transaction.teamInfo?.name || transaction.teamName || transaction.team_name || transaction.team || 'N/A',
        date: transaction.paidAt || transaction.createdAt || transaction.created_at || transaction.date || new Date().toISOString(),
        ageCategory: transaction.ageCategory || transaction.age_category || 'N/A',
        amount: transaction.amount || 0,
        paymentMode: transaction.method || transaction.paymentMode || transaction.payment_mode || transaction.paymentMethod || 'N/A',
        status: (transaction.paidStatus || transaction.status || 'pending').toLowerCase()
      };
      
      console.log('Dashboard - Mapped transaction:', mappedTransaction);
      return mappedTransaction;
    });
  } catch (error: any) {
    console.error('Failed to fetch latest transactions:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error status:', error.response?.status);
    console.error('Request URL:', error.config?.url);
    console.error('Request headers:', error.config?.headers);
    
    // Handle 404 errors as "no data available"
    if (error.response?.status === 404) {
      console.log('No latest transactions found in the system - returning empty array');
      return [];
    }
    
    // For other errors, return empty array instead of mock data
    console.error('Other error occurred, returning empty array');
    return [];
  }
};

// Fetch recent transactions for Super Admin Dashboard
export const getSuperAdminRecentTransactions = async (): Promise<Transaction[]> => {
  try {
    const response = await secureApi.get<any>(`/api/v1/payments/tournament-registration/recent-transactions`);
    
    // Handle API response structure: may be wrapped in { statusCode, data: {...} } or direct
    let transactions = [] as any[];
    if ((response as any)?.statusCode === 200 && (response as any)?.status === 'success') {
      transactions = (response as any)?.data?.transactions || (response as any)?.data || [];
    } else if ((response as any)?.transactions) {
      // Direct transactions array in response
      transactions = (response as any).transactions;
    } else if (Array.isArray(response)) {
      // Direct array response
      transactions = response;
    } else {
      console.error('Unexpected response structure for Super Admin recent transactions:', response);
      throw new Error((response as any).message || 'Failed to fetch Super Admin recent transactions');
    }
    
    
    // Transform the response to match the expected format
    return transactions.map((transaction: any) => {
      
      const mappedTransaction = {
        _id: transaction._id || transaction.id || `temp-${Date.now()}`,
        transactionId: transaction.paymentId || transaction.transactionId || transaction.transaction_id || transaction.id || transaction._id || 'N/A',
        tournamentName: transaction.tournamentInfo?.name || transaction.tournamentName || transaction.tournament_name || transaction.tournament || 'N/A',
        teamName: transaction.teamInfo?.name || transaction.teamName || transaction.team_name || transaction.team || 'N/A',
        date: transaction.paidAt || transaction.createdAt || transaction.created_at || transaction.date || new Date().toISOString(),
        ageCategory: transaction.ageCategory || transaction.age_category || 'N/A',
        amount: transaction.amount || 0,
        paymentMode: transaction.method || transaction.paymentMode || transaction.payment_mode || transaction.paymentMethod || 'N/A',
        status: (transaction.paidStatus || transaction.status || 'pending').toLowerCase()
      };
      
      return mappedTransaction;
    });
  } catch (error: any) {
    console.error('Failed to fetch Super Admin recent transactions:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error status:', error.response?.status);
    console.error('Request URL:', error.config?.url);
    console.error('Request headers:', error.config?.headers);
    
    // Handle 404 errors as "no data available"
    if (error.response?.status === 404) {
      console.log('No Super Admin recent transactions found in the system - returning empty array');
      return [];
    }
    
    // For other errors, return empty array instead of mock data
    console.error('Other error occurred, returning empty array');
    return [];
  }
};

// Fetch recent players for a club
export const getRecentPlayers = async (clubId: string): Promise<any[]> => {
  try {
    console.log('Dashboard - API URL:', `/api/v1/club/recent-players/${clubId}`);
    console.log('Dashboard - Club ID:', clubId);

    const response = await secureApi.get<any>(`/api/v1/club/recent-players/${clubId}`);
    
    console.log('Dashboard - Recent players API response received:', response);
    
    // Handle different response structures
    let players = [] as any[];
    if ((response as any)?.statusCode === 200 && (response as any)?.status === 'success') {
      players = (response as any)?.data?.players || (response as any)?.data || [];
    } else if ((response as any)?.players) {
      // Direct players array in response
      players = (response as any).players;
    } else if (Array.isArray(response)) {
      // Direct array response
      players = response;
    } else {
      console.error('Unexpected response structure for recent players:', response);
      throw new Error((response as any).message || 'Failed to fetch recent players');
    }
    
    console.log('Dashboard - Parsed players:', players);
    
    // Transform the response to match the expected format
    return players.map((player: any) => {
      console.log('Dashboard - Processing player:', player);
      
      const mappedPlayer = {
        _id: player._id || player.id || `temp-${Date.now()}`,
        name: player.name || player.fullName || player.playerName || player.firstName || 'N/A',
        course: player.ageCategory || player.course || player.category || player.ageGroup || 'N/A',
        dateOfBirth: player.dateOfBirth || player.dob || player.birthDate || player.age || 'N/A',
        sport: player.sport || player.sports || player.sportType || 'N/A',
        contact: player.contact || player.phone || player.phoneNumber || player.mobile || 'N/A',
        location: player.location || player.city || player.address || player.place || 'N/A',
        status: (player.status || player.verificationStatus || player.verifyStatus || 'pending').toLowerCase(),
        profilePic: player.profilePic || player.profilePicture || player.image || player.avatar || player.photo || ''
      };
      
      console.log('Dashboard - Mapped player:', mappedPlayer);
      return mappedPlayer;
    });
  } catch (error: any) {
    console.error('Failed to fetch recent players:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error status:', error.response?.status);
    console.error('Request URL:', error.config?.url);
    console.error('Request headers:', error.config?.headers);
    
    // Handle 404 errors as "no data available"
    if (error.response?.status === 404) {
      console.log('No recent players found in the system - returning empty array');
      return [];
    }
    
    // For other errors, return empty array instead of throwing
    console.error('Other error occurred, returning empty array');
    return [];
  }
};

// Fetch recent players for Super Admin Dashboard
export const getSuperAdminRecentPlayers = async (): Promise<any[]> => {
  try {
    const response = await secureApi.get<any>(`/api/v1/club/recent-players`);
    
    // Handle API response structure: may be wrapped in { statusCode, data: {...} } or direct
    let players = [] as any[];
    if ((response as any)?.statusCode === 200 && (response as any)?.status === 'success') {
      players = (response as any)?.data?.players || (response as any)?.data || [];
    } else if ((response as any)?.players) {
      // Direct players array in response
      players = (response as any).players;
    } else if (Array.isArray(response)) {
      // Direct array response
      players = response;
    } else {
      console.error('Unexpected response structure for Super Admin recent players:', response);
      throw new Error((response as any).message || 'Failed to fetch Super Admin recent players');
    }
    
    
    // Transform the response to match the expected format
    return players.map((player: any) => {
      
      const mappedPlayer = {
        _id: player._id || player.id || `temp-${Date.now()}`,
        name: player.name || player.fullName || player.playerName || player.firstName || 'N/A',
        course: player.ageCategory || player.course || player.category || player.ageGroup || 'N/A',
        dateOfBirth: player.dob || player.dateOfBirth || player.birthDate || player.age || 'N/A',
        sport: player.sport || player.sports || player.sportType || 'N/A',
        contact: player.contact || player.phone || player.phoneNumber || player.mobile || 'N/A',
        location: player.location || player.city || player.address || player.place || 'N/A',
        status: (player.verificationStatus || player.status || player.verifyStatus || 'pending').toLowerCase(),
        profilePic: player.profilePic || player.profilePicture || player.image || player.avatar || player.photo || '',
        teamInfo: player.teamInfo || null
      };
      
      return mappedPlayer;
    });
  } catch (error: any) {
    console.error('Failed to fetch Super Admin recent players:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error status:', error.response?.status);
    console.error('Request URL:', error.config?.url);
    console.error('Request headers:', error.config?.headers);
    
    // Handle 404 errors as "no data available"
    if (error.response?.status === 404) {
      console.log('No Super Admin recent players found in the system - returning empty array');
      return [];
    }
    
    // For other errors, return empty array instead of mock data
    console.error('Other error occurred, returning empty array');
    return [];
  }
};

// Fetch recent teams for Super Admin Dashboard
export const getSuperAdminRecentTeams = async (): Promise<any[]> => {
  try {
    const response = await secureApi.get<any>(`/api/v1/club/recent-teams`);
    
    // Handle API response structure: may be wrapped in { statusCode, data: {...} } or direct
    let teams = [] as any[];
    if ((response as any)?.statusCode === 200 && (response as any)?.status === 'success') {
      teams = (response as any)?.data?.teams || (response as any)?.data || [];
    } else if ((response as any)?.teams) {
      // Direct teams array in response
      teams = (response as any).teams;
    } else if (Array.isArray(response)) {
      // Direct array response
      teams = response;
    } else {
      console.error('Unexpected response structure for Super Admin recent teams:', response);
      throw new Error((response as any).message || 'Failed to fetch Super Admin recent teams');
    }
    
    
    // Transform the response to match the expected format
    return teams.map((team: any) => {
      
      const mappedTeam = {
        _id: team._id || team.id || `temp-${Date.now()}`,
        name: team.name || team.teamName || 'N/A',
        shortName: team.shortName || team.short_name || team.abbreviation || 'N/A',
        logo: normalizeLogoUrl(team),
        managerName: team.managerName || team.manager || team.coach || 'N/A',
        managerContactNum: team.managerContactNum || team.contact || team.phone || 'N/A',
        ageCategory: team.ageCategory || team.category || team.ageGroup || 'N/A',
        createdAt: team.createdAt || team.created_at || team.dateCreated || 'N/A',
        playersCount: team.playersCount || team.playerCount || team.totalPlayers || 0
      };
      
      return mappedTeam;
    });
  } catch (error: any) {
    console.error('Failed to fetch Super Admin recent teams:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error status:', error.response?.status);
    console.error('Request URL:', error.config?.url);
    console.error('Request headers:', error.config?.headers);
    
    // Handle 404 errors as "no data available"
    if (error.response?.status === 404) {
      console.log('No Super Admin recent teams found in the system - returning empty array');
      return [];
    }
    
    // For other errors, return empty array instead of mock data
    console.error('Other error occurred, returning empty array');
    return [];
  }
};

// Fetch recent clubs for Super Admin Dashboard
export const getSuperAdminRecentClubs = async (): Promise<any[]> => {
  try {
    const response = await secureApi.get<any>(`/api/v1/club/recent-clubs`);
    
    // Handle API response structure: may be wrapped in { statusCode, data: {...} } or direct
    let clubs = [] as any[];
    if ((response as any)?.statusCode === 200 && (response as any)?.status === 'success') {
      clubs = (response as any)?.data?.clubs || (response as any)?.data || [];
    } else if ((response as any)?.clubs) {
      // Direct clubs array in response
      clubs = (response as any).clubs;
    } else if (Array.isArray(response)) {
      // Direct array response
      clubs = response;
    } else {
      console.error('Unexpected response structure for Super Admin recent clubs:', response);
      throw new Error((response as any).message || 'Failed to fetch Super Admin recent clubs');
    }
    
    
    // Transform the response to match the expected format
    return clubs.map((club: any) => {
      
      const mappedClub = {
        _id: club._id || club.id || `temp-${Date.now()}`,
        name: club.name || club.clubName || 'N/A',
        logo: normalizeLogoUrl(club),
        emailId: club.emailId || club.email || club.contactEmail || 'N/A',
        managerName: club.managerName || club.manager || club.owner || 'N/A',
        countryCode: club.countryCode || club.country_code || 91,
        managerContactNum: club.managerContactNum || club.contact || club.phone || 'N/A',
        location: club.location || club.city || club.address || club.place || 'N/A'
      };
      
      return mappedClub;
    });
  } catch (error: any) {
    console.error('Failed to fetch Super Admin recent clubs:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error status:', error.response?.status);
    console.error('Request URL:', error.config?.url);
    console.error('Request headers:', error.config?.headers);
    
    // Handle 404 errors as "no data available"
    if (error.response?.status === 404) {
      console.log('No Super Admin recent clubs found in the system - returning empty array');
      return [];
    }
    
    // For other errors, return empty array instead of mock data
    console.error('Other error occurred, returning empty array');
    return [];
  }
};

// Fetch complete dashboard data for a club
export const getDashboardData = async (clubId: string): Promise<DashboardData> => {
  try {
    // Fetch all dashboard data in parallel
    const [stats, upcomingTournaments, ongoingTournaments, latestTransactions, recentPlayers] = await Promise.all([
      getDashboardStats(clubId),
      getUpcomingTournamentsForDashboard(), // Changed to getUpcomingTournamentsForDashboard
      getOngoingTournaments(clubId),
      getLatestTransactions(clubId),
      getRecentPlayers(clubId)
    ]);
    
    return {
      stats,
      upcomingTournaments,
      ongoingTournaments,
      recentTransactions: latestTransactions,
      recentPlayers: recentPlayers
    };
  } catch (error) {
    console.error('Failed to fetch dashboard data:', error);
    throw error;
  }
}; 